import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import type z from 'zod';

import { FieldGroup } from '@nesso/ui/primitives/field';
import { InputField } from '@nesso/ui/primitives/input-field';

import {
    MAX_INPUT_FIELD_LENGTH,
    nameTitleStepSchema,
    type StepProps,
    useUpdateOnboardingStepOptions,
    useUpdatePracticeMemberOptions,
} from '@/entities/practice-member';
import { CardFormSection } from '@/shared/ui/forms/card-form-section';

export function OnboardingNameTitleStep(props: StepProps) {
    const form = useForm<z.infer<typeof nameTitleStepSchema>>({
        mode: 'onChange',
        resolver: zodResolver(nameTitleStepSchema),
        defaultValues: {
            preferredFirstName: props.stepData?.preferredFirstName || '',
            title: props.stepData?.title || '',
        },
    });

    const {
        formState: { isDirty, isSubmitting },
    } = form;

    const mutation = useMutation(useUpdatePracticeMemberOptions());
    const updateOnboardingStepMutation = useMutation(useUpdateOnboardingStepOptions());

    const onSubmit = async (data: z.infer<typeof nameTitleStepSchema>) => {
        try {
            if (props.nextStep) {
                updateOnboardingStepMutation.mutate(props.nextStep);
            }
            if (isDirty) {
                await mutation.mutateAsync({
                    preferredFirstName: data.preferredFirstName,
                    title: data.title || null,
                });
            }
            props.onNext();
        } catch {
            console.error('Failed to update name and title step');
        }
    };

    const handleManualSubmit = () => {
        form.handleSubmit(onSubmit)();
    };

    return (
        <CardFormSection
            title="Let’s get to know each other"
            description="What should we call you?"
            progressValue={props.progress}
            showBackButton={false}
            isFatalError={mutation.isError}
            disabledBackButton={isSubmitting}
            disabledNextButton={isSubmitting}
            onNext={handleManualSubmit}
            onBack={props.onBack}
        >
            <FormProvider {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <FieldGroup className="gap-7 sm:max-w-100">
                        <Controller
                            control={form.control}
                            name="preferredFirstName"
                            render={({ field, fieldState }) => (
                                <InputField
                                    {...field}
                                    autoFocus
                                    id={field.name}
                                    label="First name"
                                    disabled={isSubmitting}
                                    maxLength={MAX_INPUT_FIELD_LENGTH}
                                    error={fieldState.error?.message ?? fieldState.invalid}
                                />
                            )}
                        />
                        <Controller
                            control={form.control}
                            name="title"
                            render={({ field, fieldState }) => (
                                <InputField
                                    {...field}
                                    id={field.name}
                                    label="Title (optional)"
                                    description="ex. LMFT, LCSW, etc"
                                    disabled={isSubmitting}
                                    maxLength={MAX_INPUT_FIELD_LENGTH}
                                    error={fieldState.error?.message ?? fieldState.invalid}
                                />
                            )}
                        />
                    </FieldGroup>
                </form>
            </FormProvider>
        </CardFormSection>
    );
}
